import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
} from '@angular/core';
import { DsTooltipDirectiveModule, SelectListOption, TooltipConfig } from '@bmw-ds/components';

import {
  CustomerSalutation,
  CustomerTitle,
  NationalIdDocumentType,
} from '../../../customer/model/customer-information.model';
import { GCDM_MAX_LENGTH, hasForbiddenNameCharacters } from '../../../customer/model/gcdm-constraints';
import { IdentityField } from '../../../customer/model/identity-field.model';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { InputFieldComponent } from '../../../form/components/input-field/input-field.component';
import { LanguagePickerComponent } from '../../../form/components/language-picker/language-picker.component';
import { SelectFieldComponent } from '../../../form/components/select-field/select-field.component';
import { I18nService, useI18n } from '../../../i18n/services/i18n.service';
import { PersonalIdentificationNumbersComponent } from '../personal-identification-numbers/personal-identification-numbers.component';

@Component({
  selector: 'cdf-profile-edit-identity',
  imports: [
    InputFieldComponent,
    SelectFieldComponent,
    DsTooltipDirectiveModule,
    PersonalIdentificationNumbersComponent,
    LanguagePickerComponent,
  ],
  host: { class: 'd-flex flex-column gap-2x' },
  template: `
    <div class="identity-row">
      <cdf-select-field
        id="salutation"
        [(model)]="salutation"
        [placeholder]="i18n('placeholder.salutation')"
        [displayError]="invalidIdentityFields().includes(IdentityField.SALUTATION)"
        [errorMessage]="i18n('customerInformationDialog.error.emptySelect')"
        [options]="salutations()"
        [isClearable]="true"
        [disabled]="disabled()"
        [hidden]="featureVisibilityService.isHidden(IdentityField.SALUTATION)"
      />
      <cdf-select-field
        id="title"
        [(model)]="title"
        [placeholder]="i18n('placeholder.title')"
        [options]="titles()"
        [isClearable]="true"
        [disabled]="disabled()"
        [hidden]="featureVisibilityService.isHidden(IdentityField.TITLE)"
      />
    </div>

    <div class="identity-row">
      <cdf-input-field
        id="first-name"
        [(model)]="firstName"
        [placeholder]="i18n('placeholder.firstName')"
        [displayError]="invalidIdentityFields().includes(IdentityField.FIRST_NAME)"
        [errorMessage]="nameErrorMessage(firstName())"
        [disabled]="disabled() || editingIdentityDisabled()"
        [maxLength]="gcdmMaxLength.firstName"
        [ds-tooltip]="i18n('customerInformationDialog.nameDisabledTooltip')"
        [dsTooltipConfig]="disabledNameTooltipConfig()"
        [hidden]="featureVisibilityService.isHidden(IdentityField.FIRST_NAME)"
      />
      <cdf-input-field
        id="middle-name"
        [(model)]="middleName"
        [placeholder]="i18n('placeholder.middleName')"
        [displayError]="invalidIdentityFields().includes(IdentityField.MIDDLE_NAME)"
        [errorMessage]="nameErrorMessage(middleName())"
        [disabled]="disabled() || editingIdentityDisabled()"
        [maxLength]="gcdmMaxLength.middleName"
        [ds-tooltip]="i18n('customerInformationDialog.nameDisabledTooltip')"
        [dsTooltipConfig]="disabledNameTooltipConfig()"
        [hidden]="featureVisibilityService.isHidden(IdentityField.MIDDLE_NAME)"
      />
    </div>

    <div class="identity-row">
      <div class="col d-flex gap-2x">
        <cdf-input-field
          id="last-name"
          [(model)]="lastName"
          [placeholder]="i18n('placeholder.lastName')"
          [displayError]="invalidIdentityFields().includes(IdentityField.LAST_NAME)"
          [errorMessage]="nameErrorMessage(lastName())"
          [disabled]="disabled() || editingIdentityDisabled()"
          [maxLength]="gcdmMaxLength.lastName"
          [ds-tooltip]="i18n('customerInformationDialog.nameDisabledTooltip')"
          [dsTooltipConfig]="disabledNameTooltipConfig()"
          [hidden]="featureVisibilityService.isHidden(IdentityField.LAST_NAME)"
        />

        <cdf-input-field
          id="suffix"
          [(model)]="suffix"
          [placeholder]="i18n('placeholder.suffix')"
          [displayError]="invalidIdentityFields().includes(IdentityField.SUFFIX)"
          [errorMessage]="nameErrorMessage(suffix())"
          [maxLength]="gcdmMaxLength.suffix"
          [disabled]="disabled() || editingIdentityDisabled()"
          [ds-tooltip]="i18n('customerInformationDialog.nameDisabledTooltip')"
          [dsTooltipConfig]="disabledNameTooltipConfig()"
          [hidden]="featureVisibilityService.isHidden(IdentityField.SUFFIX)"
        />
      </div>

      <div class="col">
        <cdf-language-picker
          id="correspondence-language"
          class="ps-05x"
          [label]="i18n('customerComparisonField.CORRESPONDENCE_LANGUAGE')"
          [languages]="languages()"
          [language]="correspondenceLanguage() ?? ''"
          [displayError]="invalidIdentityFields().includes(IdentityField.CORRESPONDENCE_LANGUAGE)"
          [errorMessage]="i18n('customerInformationDialog.error.emptySelect')"
          (languageChange)="correspondenceLanguage.set($event)"
          [disabled]="disabled()"
          [hidden]="
            isCorrespondenceLanguageHidden() || correspondenceLanguage() === undefined || languages().length <= 1
          "
        />
      </div>
    </div>
    <div class="personal-identification-numbers">
      <cdf-personal-identification-numbers
        [(taxNumber)]="taxNumber"
        [(taxNumberCountry)]="taxNumberCountry"
        [(nationalId)]="nationalId"
        [(nationalIdDocumentType)]="nationalIdDocumentType"
        [availableTaxNumberCountries]="availableTaxNumberCountries()"
        [availableNationalIdDocumentTypes]="availableNationalIdDocumentTypes()"
        [invalidIdentificationNumberFields]="invalidIdentityFields()"
      />
    </div>
  `,
  styleUrl: './profile-edit-identity.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileEditIdentityComponent {
  protected readonly i18n = useI18n();
  protected readonly i18nService = inject(I18nService);
  protected readonly featureVisibilityService = inject(FeatureVisibilityService);

  readonly salutation = model<string>();
  readonly firstName = model<string>();
  readonly lastName = model<string>();
  readonly title = model<string>();
  readonly middleName = model<string>();
  readonly suffix = model<string>();
  readonly correspondenceLanguage = model<string>();
  readonly taxNumber = model<string>();
  readonly taxNumberCountry = model<string>();
  readonly nationalId = model<string>();
  readonly nationalIdDocumentType = model<NationalIdDocumentType>();

  readonly availableTaxNumberCountries = input.required<SelectListOption[]>();
  readonly displayMxSalutationOption = input<boolean>(true);
  readonly displayProfDrTitleOption = input<boolean>(true);
  readonly displayGbIeSalutationOptions = input<boolean>(true);
  readonly availableNationalIdDocumentTypes = input.required<NationalIdDocumentType[]>();

  readonly disabled = input(false, { transform: booleanAttribute });
  readonly editingIdentityDisabled = input(false, { transform: booleanAttribute });
  readonly invalidIdentityFields = input<IdentityField[]>([]);
  readonly languages = input<string[]>([]);
  protected readonly gcdmMaxLength = GCDM_MAX_LENGTH;

  /**
   * A name field is invalid either because it is empty or because it contains a character GCDM
   * rejects. Deriving the message from the value keeps `invalidIdentityFields` a plain field list.
   */
  protected nameErrorMessage(value: string | undefined): string {
    return hasForbiddenNameCharacters(value)
      ? this.i18n('customerInformationDialog.error.invalidNameCharacters')
      : this.i18n('customerInformationDialog.error.emptyInput');
  }

  protected readonly languageEffect = effect(() => {
    const languages = this.languages();
    if (
      this.correspondenceLanguage() === undefined &&
      !this.isCorrespondenceLanguageHidden() &&
      languages.length === 1 // is there exactly one language
    ) {
      const activeUiLanguage = this.i18nService.activeLanguage().substring(0, 2);
      const initialLanguage = languages.includes(activeUiLanguage) ? activeUiLanguage : languages[0];

      this.correspondenceLanguage.set(initialLanguage);
    }
  });

  protected readonly isCorrespondenceLanguageHidden = computed(() =>
    this.featureVisibilityService.isHidden(IdentityField.CORRESPONDENCE_LANGUAGE),
  );

  private static readonly GbIeOnlySalutations = new Set<string>([
    CustomerSalutation.MRS,
    CustomerSalutation.MISS,
    CustomerSalutation.DR,
  ]);

  /**
   * The Mx option should only be displayed in germany
   */
  protected readonly salutations = computed(() =>
    Object.values(CustomerSalutation)
      .map((salutation) => ({
        id: salutation,
        label: this.i18n.enum(salutation, 'salutations'),
      }))
      // The MX salutation should only be displayed in Germany, as determined in this component’s container.
      .filter((salutation) => salutation.id !== CustomerSalutation.MX || this.displayMxSalutationOption())
      // The Mrs, Miss and Dr salutations should only be displayed in GB/IE, as determined in this component’s container.
      .filter(
        (salutation) =>
          !ProfileEditIdentityComponent.GbIeOnlySalutations.has(salutation.id) || this.displayGbIeSalutationOptions(),
      ),
  );

  protected readonly titles = computed(() =>
    Object.values(CustomerTitle)
      .map((title) => ({
        id: title,
        label: this.i18n.enum(title, 'titles'),
      }))
      // The Prof Dr title should only be displayed in Germany, as determined in this component’s container.
      .filter((title) => title.id !== CustomerTitle.PROF_DR || this.displayProfDrTitleOption()),
  );

  protected readonly disabledNameTooltipConfig = computed(
    () =>
      ({
        origin: 'bottom',
        showTriggerDelay: 500,
        disabled: !this.editingIdentityDisabled(),
      }) satisfies TooltipConfig,
  );

  protected readonly IdentityField = IdentityField;
}
